import {NavigateFunction} from 'react-router-dom';
import {savaquanFetchJson} from '../../../shared/services/http.service';
import {SavaquanHttpError} from '../../../shared/utils/http-error';
import {
  AddLibraryOperationRequestDto, ChangeLibraryWorkItemTypeRequestDto, LibraryDecisionResponseDto, LibraryEstimatePageDto, LibraryEstimateStateDto,
  LibraryOperationChangeResponseDto, LibraryView, LibraryWorkItemTypeChangeResponseDto, RemoveLibraryOperationRequestDto, SaveLibraryRecordRequestDto,
  SaveLibraryWorkItemRequestDto, SaveLibraryWorkItemResponseDto,
} from '../../../shared/dtos/comparable-library.dto';
import {WorkPlanCatalogDto} from '../../../shared/dtos/work-plan-catalog.dto';

const root = `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/library`;

let workPlanCatalog: Promise<WorkPlanCatalogDto> | undefined;

/** The Work Plan question catalog, fetched once per page load (the API serves one copy for every client). */
export const getWorkPlanCatalog = (navigate: NavigateFunction): Promise<WorkPlanCatalogDto> => {
  workPlanCatalog ??= savaquanFetchJson<WorkPlanCatalogDto>(`${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/work-plan/catalog`, {navigate})
    .catch(err => { workPlanCatalog = undefined; throw err; });
  return workPlanCatalog;
};

const send = <T>(navigate: NavigateFunction, method: 'POST' | 'PUT', path: string, body: unknown, signal?: AbortSignal) =>
  savaquanFetchJson<T>(`${root}${path}`, {navigate, method, body: JSON.stringify(body), signal});

export const getLibraryEstimates = (
  navigate: NavigateFunction, view: LibraryView, search: string, page: number, signal?: AbortSignal,
) => {
  const params = new URLSearchParams({view, page: String(page), pageSize: '25'});
  if (search.trim()) params.set('search', search.trim());
  return savaquanFetchJson<LibraryEstimatePageDto>(`${root}/estimates?${params}`, {navigate, signal});
};

export const getLibraryEstimate = (navigate: NavigateFunction, estimateId: number, signal?: AbortSignal) =>
  savaquanFetchJson<LibraryEstimateStateDto>(`${root}/estimates/${estimateId}`, {navigate, signal});

export const saveLibraryWorkItem = (navigate: NavigateFunction, estimateId: number, treeId: number, body: SaveLibraryWorkItemRequestDto) =>
  send<SaveLibraryWorkItemResponseDto>(navigate, 'PUT', `/estimates/${estimateId}/work-items/${treeId}`, body);

/** A new operation on a work item, its price and time taken from the others (legacy-record editor). */
export const addLibraryOperation = (navigate: NavigateFunction, estimateId: number, treeId: number, body: AddLibraryOperationRequestDto) =>
  send<LibraryOperationChangeResponseDto>(navigate, 'POST', `/estimates/${estimateId}/work-items/${treeId}/operations`, body);

/** Removes a work item's operation, its price and time given to the others (legacy-record editor). */
export const removeLibraryOperation = (
  navigate: NavigateFunction, estimateId: number, treeId: number, operationId: number, body: RemoveLibraryOperationRequestDto,
) => send<LibraryOperationChangeResponseDto>(navigate, 'POST', `/estimates/${estimateId}/work-items/${treeId}/operations/${operationId}/removal`, body);

/** A work item's type changed in place, or (preview) what that would do (SQ-671). */
export const changeLibraryWorkItemType = (
  navigate: NavigateFunction, estimateId: number, treeId: number, body: ChangeLibraryWorkItemTypeRequestDto, signal?: AbortSignal,
) => send<LibraryWorkItemTypeChangeResponseDto>(navigate, 'POST', `/estimates/${estimateId}/work-items/${treeId}/type`, body, signal);

/** The estimate's crew and cost items (legacy-record editor); answers with the whole state. */
export const saveLibraryRecord = (navigate: NavigateFunction, estimateId: number, body: SaveLibraryRecordRequestDto) =>
  send<LibraryDecisionResponseDto>(navigate, 'PUT', `/estimates/${estimateId}/record`, body);

export const setLibraryExclusion = (navigate: NavigateFunction, estimateId: number, treeId: number, excluded: boolean, requestId: string) =>
  send<LibraryDecisionResponseDto>(navigate, 'PUT', `/estimates/${estimateId}/work-items/${treeId}/exclusion`, {requestId, excluded});

export const approveLibraryEstimate = (
  navigate: NavigateFunction, estimateId: number, includedOperationIds: number[], approveWithoutLaborTime: boolean, requestId: string,
) => send<LibraryDecisionResponseDto>(navigate, 'POST', `/estimates/${estimateId}/approval`, {requestId, includedOperationIds, approveWithoutLaborTime});

export const removeLibraryEstimate = (navigate: NavigateFunction, estimateId: number, requestId: string) =>
  send<LibraryDecisionResponseDto>(navigate, 'POST', `/estimates/${estimateId}/removal`, {requestId});

/** The server's {error, message} code, e.g. "work_item_changed". */
export function libraryErrorCode(error: unknown): string | null {
  if (!(error instanceof SavaquanHttpError) || !error.details) return null;
  try {
    return JSON.parse(error.details).error ?? null;
  } catch {
    return null;
  }
}

export function newRequestId(): string {
  return crypto.randomUUID();
}
